import { lang } from '../localization'

function addCommaToPriceUnsigned(price: string | number) {
  if (price === 0) {
    return price
  }
  if (!price) {
    return ''
  }
  const splittedPrice = price.toString().split('.')
  if (splittedPrice[0].toString().length > 3) {
    const formattedPrice = splittedPrice[0]
      .toString()
      .split('')
      .reverse()
      .join('')
      .replace(/.{3}/g, '$&,')
      .split('')
      .reverse()
      .join('')
    if (formattedPrice.toString().charAt(0) === ',') {
      return `${formattedPrice.substr(1)}${
        splittedPrice[1] ? `.${splittedPrice[1]}` : ''
      }`
    }
    return `${formattedPrice}${splittedPrice[1] ? `.${splittedPrice[1]}` : ''}`
  }
  return price
}

export function addCommaToPrice(price: string | number) {
  if (price && price.toString().length > 0 && price.toString()[0] === '-') {
    if (lang === 'fa') {
      return `${addCommaToPriceUnsigned(price.toString().substring(1))}-`
    } else {
      return `-${addCommaToPriceUnsigned(price.toString().substring(1))}`
    }
  }
  return addCommaToPriceUnsigned(price)
}

export function removeCommaFromPrice(price: string | number) {
  return price.toString().replaceAll(',', '')
}

// this function gets an string as input and returns true if the string is a valid number
export function isValidNumber(numberStr: string) {
  return /^\d+$/.test(numberStr)
}

export function convertPersianNumbersToEnglishNumbers(num: string) {
  return num
    .replace(/[۰-۹]/g, (chr: string) => {
      const persian = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
      return persian.indexOf(chr).toString()
    })
    .replaceAll('٪', '%')
}
export function convertToNumber(num: string) {
  return num.replace(/\D/g, '')
}

const ordinalMap: Record<string, Record<number, string>> = {
  en: {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
    6: 'sixth',
    7: 'seventh',
    8: 'eighth',
    9: 'ninth',
    10: 'tenth'
  },
  fa: {
    1: 'اول',
    2: 'دوم',
    3: 'سوم',
    4: 'چهارم',
    5: 'پنجم',
    6: 'ششم',
    7: 'هفتم',
    8: 'هشتم',
    9: 'نهم',
    10: 'دهم'
  }
}

export function getOrdinal(index: number, l = 'en'): string {
  const num = index + 1
  return ordinalMap[l][num] || num.toString()
}

/**
 * Whether a credit limit is a value the server will accept.
 *
 * **Zero is valid.** This exists because the creditor edit modal previously
 * disabled Save on `!maxCredit || !maxReservable`, which is true for zero — and a
 * creditor whose limits are zero is the overwhelmingly common case: 38 of the 47
 * creditors on stage have `max_credit = 0` and `max_reservable = 0`. Those are the
 * wallets an operator most wants to give credit to, and they were the ones that
 * could not be edited. Typing a credit amount did not help either, because the
 * other field stayed zero and kept the button disabled, with nothing on screen
 * saying why.
 *
 * Zero is also a legitimate value to save TO — setting a limit to zero revokes
 * credit — so rejecting it broke that direction as well.
 *
 * The guard was reaching for "the field is empty", but it cannot see that: the
 * inputs run their value through `Number()`, so an empty field and a zero are both
 * `0` by the time they reach state, and the field re-renders as "0" rather than
 * blank. What is actually worth rejecting is a value that is not a number, or one
 * below zero.
 */
export function isValidCreditLimit(value: number): boolean {
  return Number.isFinite(value) && value >= 0
}
